import { dispatchAction } from "../state/dispatchAction";
import { setMaintenance, setLoading } from "../../reducers/configSlice";
import { apiGetDeviceMaintenance } from "../../api/actions";
import { STORAGE } from "../storage";
import moment from "moment";
import moment_tz from "moment-timezone";
import { Store } from "../../reducers/store";

/**
 * Maintenance Service for Soketi
 * 
 * Handles fetching and managing maintenance data for Soketi implementation.
 * Similar to configService and bookingListService patterns.
 */

export const fetchDeviceMaintenance = async () => {
    try {
        const result = await apiGetDeviceMaintenance();

        if (result.error) {
            console.error('MAINTENANCE_SERVICE - Error fetching device maintenance:', result.error);
            return false;
        }

        const maintenanceResponse = result.response;
        if (Object?.keys(result?.response)?.length) {
            await handleMaintenanceResponse(maintenanceResponse);
            return true;
        }

        // No maintenance data - clear any existing maintenance
        await handleMaintenanceResponse(null);
        return true;
    } catch (error) {
        console.error('MAINTENANCE_SERVICE - Error fetching device maintenance:', error);
        return false;
    }
};

/**
 * Handle maintenance response the same way as Socket.IO
 * This replicates the logic from handleConfig.js maintenance listener for Soketi
 */
const handleMaintenanceResponse = async (maintenanceResponse) => {
    try {
        const state = Store.getState();
        const config = state.data.config;

        if (!config || !config.timezone) {
            console.warn('MAINTENANCE_SERVICE - No config or timezone available');
            dispatchAction(setMaintenance(null));
            await STORAGE.delete('currentMaintenance');
            return;
        }

        // If no maintenance data, clear maintenance state
        if (!maintenanceResponse) {
            dispatchAction(setMaintenance(null));
            await STORAGE.delete('currentMaintenance');
            return;
        }

        // Check if maintenance is currently active (same logic as Socket.IO)
        const currentMoment = moment().tz(config.timezone);
        const startMoment = moment_tz.tz(maintenanceResponse.start, config.timezone).utc().tz(config.timezone);
        const endMoment = moment_tz.tz(maintenanceResponse.end, config.timezone).utc().tz(config.timezone);

        const isMaintenanceActive = currentMoment.isSameOrAfter(startMoment) && currentMoment.isBefore(endMoment);

        if (isMaintenanceActive) {
            dispatchAction(setMaintenance(maintenanceResponse));
            
            // Store maintenance in local storage for interval checking
            await STORAGE.set('currentMaintenance', JSON.stringify(maintenanceResponse));
        } else {
            dispatchAction(setMaintenance(null));
            
            // Store maintenance for future interval checking
            await STORAGE.set('currentMaintenance', JSON.stringify(maintenanceResponse));
        }

    } catch (error) {
        console.error('MAINTENANCE_SERVICE - Error handling maintenance response:', error);
        dispatchAction(setMaintenance(null));
        await STORAGE.delete('currentMaintenance');
    }
};

/**
 * Check if stored maintenance should be active now
 * Used by the maintenance interval to check maintenance status
 */
export const checkMaintenanceStatus = async () => {
    try {
        const storedMaintenanceStr = await STORAGE.get('currentMaintenance');
        if (!storedMaintenanceStr) {
            // No stored maintenance, ensure Redux state is clear
            const state = Store.getState();
            if (state.data.maintenance) {
                dispatchAction(setMaintenance(null));
            }
            return;
        }

        const storedMaintenance = JSON.parse(storedMaintenanceStr);

        // Check if the parsed maintenance is empty or invalid
        if (!storedMaintenance || typeof storedMaintenance !== 'object' ||
            Object.keys(storedMaintenance).length === 0 ||
            !storedMaintenance.start || !storedMaintenance.end) {
            const state = Store.getState();
            if (state.data.maintenance) {
                dispatchAction(setMaintenance(null));
            }
            await STORAGE.delete('currentMaintenance');
            return;
        }

        const state = Store.getState();
        const config = state.data.config;

        if (!config || !config.timezone) {
            console.warn('MAINTENANCE_SERVICE - No config or timezone available for status check');
            return;
        }

        // Check if maintenance should be active now
        const currentMoment = moment().tz(config.timezone);
        const startMoment = moment_tz.tz(storedMaintenance.start, config.timezone).utc().tz(config.timezone);
        const endMoment = moment_tz.tz(storedMaintenance.end, config.timezone).utc().tz(config.timezone);

        const shouldBeActive = currentMoment.isSameOrAfter(startMoment) && currentMoment.isBefore(endMoment);
        const isCurrentlyActive = !!state.data.maintenance;

        // Update Redux state if there's a mismatch
        if (shouldBeActive && !isCurrentlyActive) {
            dispatchAction(setMaintenance(storedMaintenance));
        } else if (!shouldBeActive && isCurrentlyActive) {
            dispatchAction(setMaintenance(null));
        }

        // Clean up expired maintenance from storage
        if (currentMoment.isAfter(endMoment)) {
            await STORAGE.delete('currentMaintenance');
        }

    } catch (error) {
        console.error('MAINTENANCE_SERVICE - Error checking maintenance status:', error);
        // Clear maintenance state and storage on any error
        dispatchAction(setMaintenance(null));
        await STORAGE.delete('currentMaintenance');
    }
};

/**
 * Clear invalid maintenance data from storage
 * This can be called to clean up corrupted or empty maintenance data
 */
export const clearMaintenanceData = async () => {
    try {
        dispatchAction(setMaintenance(null));
        await STORAGE.delete('currentMaintenance');
    } catch (error) {
        console.error('MAINTENANCE_SERVICE - Error clearing maintenance data:', error);
    }
};

export default {
    fetchDeviceMaintenance,
    checkMaintenanceStatus,
    clearMaintenanceData
};
